import {
  createColumnHelper, PaginationState,SortingState
} from '@tanstack/table-core';
import clsx from 'clsx';
import React, { useCallback,useMemo, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { toast,ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import ModalMetricCreate from '@/app/evaluations/_modalMetricCreate/modalMetricCreate';
import { Button } from '@/components/button/button';
import { DeprecationToggle } from '@/components/deprecationToggle/deprecationToggle';
import { Icon } from '@/components/icon/icon';
import { Input } from '@/components/input/input';
import { Loader } from '@/components/loader/loader';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { useNativeTable } from '@/components/nativeTable/useNativeTable';
import { buildColumnClassString, ColumnModEnums } from '@/components/table/table';
import { Heading } from '@/components/typography/heading';
import { useCreateMetricMutation, useMetricsSearch } from '@/hooks/queries/useMetrics';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';
import { MetricConfig, MetricCreateReq } from '@/types/evals';
import { dateFilterToISO, OutputFormatEnum, TimeFormatter, TimeSourceTypeEnum } from '@/utils/timeFormatter';

import styles from './page.module.scss';

const columnHelper = createColumnHelper<MetricConfig>();
const SERVER_SORT_COLUMNS: Record<string, string> = { name: 'name', createdAt: 'created_at' };

export default function EvaluationsMetricPage() {
  const navigate = useNavigate();
  const [ showModalCreate, setShowModalCreate ] = useState<boolean>();
  const [ useLocalZone ] = useLocalZonePreference();
  const [ showDeprecated, setShowDeprecated ] = useState<boolean>( false );
  const [ sorting, setSorting ] = useState<SortingState>( [ { id: 'createdAt', desc: true } ] );
  const [ pagination, setPagination ] = useState<PaginationState>( {
    pageIndex: 0,
    pageSize: 30
  } );
  const [ nameFilter, setNameFilter ] = useState( '' );
  const [ startDate, setStartDate ] = useState( '' );
  const [ endDate, setEndDate ] = useState( '' );
  const [ committedFilters, setCommittedFilters ] = useState( {
    name: '',
    startDate: '',
    endDate: ''
  } );

  const activeSort = sorting[0] as SortingState[0] | undefined;

  const { data, isLoading: isLoadingMetrics, isError } = useMetricsSearch( {
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    name: committedFilters.name || undefined,
    is_deprecated: showDeprecated ? undefined : false,
    created_at_from: committedFilters.startDate ? dateFilterToISO( committedFilters.startDate, useLocalZone ) : undefined,
    created_at_to: committedFilters.endDate ? dateFilterToISO( committedFilters.endDate, useLocalZone, true ) : undefined,
    sort_by: activeSort ? SERVER_SORT_COLUMNS[ activeSort.id ] : undefined,
    sort_order: activeSort ? ( activeSort.desc ? 'desc' : 'asc' ) : undefined,
  } );

  const pageItems = data?.items ?? [];
  const createMetricMutation = useCreateMetricMutation();

  /* COLUMNS */
  const columns = useMemo( () => [
    columnHelper.accessor( ( row ) => row.name, {
      id: 'name',
      header: 'Name',
      size: 300,
      cell: info => {
        const metric = info.row.original;
        return (
          <span data-testid={ `metric-name-${ metric.metricProviderId }` }>
            {info.getValue()}
            { metric.isDeprecated && (
              <span className='deprecated_tag'>(deprecated)</span>
            ) }
          </span>
        );
      },
      enableSorting: true,
      meta: {
        classStr: buildColumnClassString( { width: 30, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } ),
    columnHelper.accessor( ( row ) => row.description, {
      id: 'description',
      header: 'Description',
      size: 250,
      cell: info => {
        const metric = info.row.original;
        return (
          <span data-testid={ `metric-description-${ metric.metricProviderId }` }>
            {info.getValue() || '-'}
          </span>
        );
      },
      enableSorting: false,
      meta: {
        classStr: buildColumnClassString( { width: 25, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } ),
    columnHelper.accessor(
      ( row ) => row.createdAt,
      {
        id: 'createdAt',
        header: 'Created',
        size: 250,
        cell: info => {
          const value = info.getValue();
          return value ? (
            <TimeFormatter
              sourceType={ TimeSourceTypeEnum.ISO }
              value={ value }
              outputFormat={ OutputFormatEnum.STRING_SINGLE }
              opts={ { useLocalZone } }
            />
          ) : (
            <span>-</span>
          );
        },
        enableSorting: true,
        meta: {
          classStr: buildColumnClassString( { width: 25, styles: ColumnModEnums.NO_OVERFLOW } )
        }
      } ),
    columnHelper.accessor( ( row ) => row.latestVersionNumber, {
      id: 'latestVersion',
      header: 'Latest version',
      size: 150,
      cell: info => {
        const metric = info.row.original;
        return (
          <span data-testid={ `metric-version-${ metric.metricProviderId }` }>
            {info.getValue()}
          </span>
        );
      },
      enableSorting: false,
      meta: {
        classStr: buildColumnClassString( { width: 10, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } )
  ], [ useLocalZone ] );

  const handleRowClick = useCallback( ( metric: MetricConfig ) => {
    const params = new URLSearchParams( {
      metricProviderId: metric.metricProviderId,
      version: String( metric.latestVersionNumber )
    } );
    navigate( `/evaluations/metric?${ params.toString() }` );
  }, [ navigate ] );

  const tableState = useMemo( () => ( {
    sorting,
    pagination
  } ), [ sorting, pagination ] );

  // const table = useNativeTable<MetricProvider>( {
  const table = useNativeTable<MetricConfig>( {
    data: pageItems,
    columns,
    state: tableState,
    onSortingChange: ( updater ) => {
      setSorting( updater );
      setPagination( prev => ( { ...prev, pageIndex: 0 } ) );
    },
    onPaginationChange: setPagination,
    manualPagination: true,
    manualSorting: true,
    rowCount: data?.total ?? 0
  } );

  const handleApplyFilters = useCallback( () => {
    setCommittedFilters( { name: nameFilter, startDate, endDate } );
    setPagination( prev => ( { ...prev, pageIndex: 0 } ) );
  }, [ nameFilter, startDate, endDate ] );

  const handleClearFilters = useCallback( () => {
    setNameFilter( '' );
    setStartDate( '' );
    setEndDate( '' );
    setCommittedFilters( { name: '', startDate: '', endDate: '' } );
    setPagination( prev => ( { ...prev, pageIndex: 0 } ) );
  }, [] );

  /* HANDLERS */
  const onMetricCreate = ( args: MetricCreateReq ) => {
    createMetricMutation.mutate(
      args,
      {
        onSuccess: () => {
          toast.success( 'Metric created successfully' );
          setShowModalCreate( false );
        },
        onError: ( error ) => {
          console.error( 'Failed to create metric:', error );
          toast.error( error instanceof Error ? error.message : 'Failed to create metric' );
        }
      }
    );
  };

  const isLoading = isLoadingMetrics;
  const isPending = createMetricMutation.isPending;

  return (
    <div className="page" data-testid="metrics-page">
      <ToastContainer />
      <>
        {showModalCreate && (
          <ModalMetricCreate
            cb={ onMetricCreate }
            onClose={ () => {
              if ( !isPending ) {
                setShowModalCreate( false );
              }
            } }
            isSubmitting={ isPending }
          />
        )}

        <div className={ clsx( 'layout__row', 'pad', 'layout__info' ) }>
          <span className='info_name'>
            <Heading as="h2" data-testid="metrics-heading">Metrics</Heading>
          </span>
          <span className='info_actions'>
            <DeprecationToggle
              entityNamePlural='metrics'
              showDeprecated={ showDeprecated }
              onChange={ ( next ) => {
                setShowDeprecated( next );
                setPagination( prev => ( { ...prev, pageIndex: 0 } ) );
              } }
            />
            <Button
              variant='primary'
              icon={ <Icon icon={ FiPlus } /> }
              data-testid="create-metric-button"
              onClick={ () => setShowModalCreate( !showModalCreate ) }
            >
              Create Metric
            </Button>
          </span>
        </div>

        {!isLoading && !isError && (
          <div className={ clsx( 'layout__row', 'pad', 'filters' ) }>
            <div className='filter_field'>
              <Input
                id="name-filter"
                type='text'
                label='Filter by name'
                placeholder='Filter by name...'
                value={ nameFilter }
                onChange={ ( e ) => setNameFilter( e.target.value ) }
                onKeyDown={ ( e ) => {
                  if ( e.key === 'Enter' ) {
                    handleApplyFilters();
                  }
                } }
              />
            </div>
            <div className='filter_field'>
              <label htmlFor="start-date-filter" className='filter_label'>Start date</label>
              <input
                id="start-date-filter"
                type='date'
                value={ startDate }
                onChange={ ( e ) => setStartDate( e.target.value ) }
                onKeyDown={ ( e ) => {
                  if ( e.key === 'Enter' ) {
                    handleApplyFilters();
                  }
                } }
                className='filter_input'
                title="Filter by start date"
              />
            </div>
            <div className='filter_field'>
              <label htmlFor="end-date-filter" className='filter_label'>End date</label>
              <input
                id="end-date-filter"
                type='date'
                value={ endDate }
                onChange={ ( e ) => setEndDate( e.target.value ) }
                onKeyDown={ ( e ) => {
                  if ( e.key === 'Enter' ) {
                    handleApplyFilters();
                  }
                } }
                className='filter_input'
                title="Filter by end date"
              />
            </div>
            <Button
              variant='secondary'
              onClick={ handleClearFilters }
              title="Clear all filters"
              data-testid="clear-filters-button"
            >
              Clear Filters
            </Button>
            <Button
              variant='primary'
              onClick={ handleApplyFilters }
              title="Apply filters"
              data-testid="apply-filters-button"
            >
              Apply Filters
            </Button>
          </div>
        )}

        {isLoading ? (
          <div className={ clsx( 'layout__row', styles.layout__evals ) }>
            <Loader />
          </div>
        ) : (
          <div data-testid="metrics-table-container">
            <NativeTable table={ table } tbodyTestId="table-body-metrics" onRowClick={ handleRowClick } />
          </div>
        )}
        {isPending && (
          <div data-testid="loading-overlay">
            <Loader />
          </div>
        )}
      </>
    </div>
  );
}
